import { orderFileFields } from "./orderFileFields";

/**
 * Takes the raw form values and produces the final file payload to send to the backend.
 * This is used by both the normal submit flow and the "skip validation" flow to guarantee
 * they produce identical payloads (previously the logic was duplicated and drifted).
 */
export const buildFilePayload = values => {
  const fileData = JSON.parse(JSON.stringify(values.file));

  delete fileData.allow_concurrent_events;

  if (fileData.output_size !== undefined && fileData.output_size !== null) {
    fileData.output_size = String(fileData.output_size);
  }
  if (fileData.output_fps !== undefined && fileData.output_fps !== null) {
    fileData.output_fps = Number(fileData.output_fps);
  }

  if (fileData.stream_sources && Array.isArray(fileData.stream_sources)) {
    fileData.stream_sources = fileData.stream_sources.map(source => {
      // Strip UI-only fields that must never reach the backend.
      /* eslint-disable no-unused-vars */
      const {
        brand,
        model,
        camOp,
        isAudioSource,
        parentIndex,
        use,
        public_interface,
        useAutoExposure,
        _tempParams,
        ...cleanSource
      } = source;
      /* eslint-enable no-unused-vars */

      if (cleanSource.cam_id) {
        cleanSource.cam_id = cleanSource.cam_id.toLowerCase();
      }

      if (cleanSource.steerable) {
        if (cleanSource.steerable.method) {
          cleanSource.steerable = {
            method: "mechanical",
            auto: true,
            cfg: "",
            params: "-m",
            ...cleanSource.steerable,
          };
        } else {
          delete cleanSource.steerable;
        }
      }

      if (cleanSource.label?.startsWith("vptz")) {
        delete cleanSource.exp_camera_cfg;
        delete cleanSource.exp_camera_url;
      }

      // "auto" is a UI-only concept for the default stream type, never a real value.
      // Only strip when unset or "auto" — any explicit value (including "stream" or
      // even "", which the validation only allows through for test templates) means
      // the loaded template already had the field, so it must be respected as-is.
      if (cleanSource.type === undefined || cleanSource.type === "auto") {
        delete cleanSource.type;
      }

      return cleanSource;
    });
  }

  if (fileData.video_drains?.recordings && Array.isArray(fileData.video_drains.recordings)) {
    fileData.video_drains.recordings = fileData.video_drains.recordings.map(recording => {
      if (recording.src === "PAN") {
        return { ...recording, chunk_type: "fmp4" };
      }
      return recording;
    });
  }

  if (fileData.mixer?.score) {
    if (!["drawn", "burnt"].includes(fileData.mixer.score.default)) {
      // Score default is "none", undefined, or invalid — strip the score block entirely.
      // eslint-disable-next-line no-unused-vars
      const { score, ...restMixer } = fileData.mixer;
      fileData.mixer = restMixer;
    } else {
      if (!fileData.mixer.score.include_secondary) {
        if (fileData.mixer.score.default === "drawn") {
          // eslint-disable-next-line no-unused-vars
          const { burnt, ...restScore } = fileData.mixer.score;
          fileData.mixer.score = restScore;
        } else if (fileData.mixer.score.default === "burnt") {
          // eslint-disable-next-line no-unused-vars
          const { drawn, ...restScore } = fileData.mixer.score;
          fileData.mixer.score = restScore;
        }
      }

      if (fileData.mixer?.score) {
        // eslint-disable-next-line no-unused-vars
        const { include_secondary, ...restScore } = fileData.mixer.score;
        fileData.mixer.score = restScore;
      }
    }

    if (fileData.mixer?.score && (!fileData.mixer.score.use_titler || !fileData.mixer.score.titler_url)) {
      // eslint-disable-next-line no-unused-vars
      const { titler_url, use_titler, ...restScore } = fileData.mixer.score;
      fileData.mixer.score = restScore;
    }
  }

  if (Array.isArray(fileData.set_dns_names)) {
    fileData.set_dns_names = fileData.set_dns_names.filter(dns => dns && dns.trim() !== "");
    if (fileData.set_dns_names.length === 0) {
      delete fileData.set_dns_names;
    }
  }

  if (fileData.virtualcam) {
    if (!fileData.virtualcam.sky_dome || fileData.virtualcam.sky_dome.trim() === "") {
      delete fileData.virtualcam.sky_dome;
    }
    if (!fileData.virtualcam.scheduler || fileData.virtualcam.scheduler.length === 0) {
      delete fileData.virtualcam.scheduler;
    }
  }

  return orderFileFields(fileData);
};
